import { useState } from 'react'
import Button from '@/components/ui/Button'
import { FormItem, Form } from '@/components/ui/Form'
import PasswordInput from '@/components/shared/PasswordInput'
import { apiResetPassword } from '@/services/AuthService'
import CaptchaField from '@/components/form/fields/CaptchaField'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import type { ZodType } from 'zod'
import type { CommonProps } from '@/@types/common'

interface ResetPasswordFormProps extends CommonProps {
    token: string
    email: string
    resetComplete: boolean
    setResetComplete?: (complete: boolean) => void
    setMessage?: (message: string) => void
}

type ResetPasswordFormSchema = {
    newPassword: string
    confirmPassword: string
    recaptcha?: string | null
}

const validationSchema: ZodType<ResetPasswordFormSchema> = z
    .object({
        newPassword: z
            .string({ required_error: 'Please enter your password' })
            .min(8, 'Password must be at least 8 characters'),
        confirmPassword: z.string({
            required_error: 'Confirm Password Required',
        }),
        recaptcha: z.string().optional().nullable(),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
        message: 'Your passwords do not match',
        path: ['confirmPassword'],
    })

const getErrorMessage = (errors: unknown) => {
    if (typeof errors === 'string') {
        return errors
    }

    if (
        errors &&
        typeof errors === 'object' &&
        'response' in errors &&
        errors.response &&
        typeof errors.response === 'object' &&
        'data' in errors.response &&
        errors.response.data &&
        typeof errors.response.data === 'object' &&
        'message' in errors.response.data &&
        typeof errors.response.data.message === 'string'
    ) {
        return errors.response.data.message
    }

    return 'Failed to reset password'
}

const ResetPasswordForm = (props: ResetPasswordFormProps) => {
    const [isSubmitting, setSubmitting] = useState<boolean>(false)
    const [captchaKey, setCaptchaKey] = useState(0)

    const {
        className,
        token,
        email,
        setMessage,
        setResetComplete,
        resetComplete,
        children,
    } = props

    const {
        handleSubmit,
        formState: { errors },
        control,
        resetField,
    } = useForm<ResetPasswordFormSchema>({
        resolver: zodResolver(validationSchema),
    })

    const resetCaptcha = () => {
        resetField('recaptcha')
        setCaptchaKey((key) => key + 1)
    }

    const onResetPassword = async (values: ResetPasswordFormSchema) => {
        const { newPassword } = values

        setSubmitting(true)

        try {
            const resp = await apiResetPassword<{ message: string }>({
                token,
                email,
                password: newPassword,
                password_confirmation: values.confirmPassword,
                recaptcha: values.recaptcha,
            })
            if (resp) {
                setSubmitting(false)
                setResetComplete?.(true)
            }
        } catch (errors) {
            setMessage?.(getErrorMessage(errors))
            resetCaptcha()
            setSubmitting(false)
        }

        setSubmitting(false)
    }

    return (
        <div className={className}>
            {!resetComplete ? (
                <Form onSubmit={handleSubmit(onResetPassword, resetCaptcha)}>
                    <FormItem
                        label="Password"
                        invalid={Boolean(errors.newPassword)}
                        errorMessage={errors.newPassword?.message}
                    >
                        <Controller
                            name="newPassword"
                            control={control}
                            render={({ field }) => (
                                <PasswordInput
                                    autoComplete="off"
                                    placeholder="••••••••••••"
                                    {...field}
                                />
                            )}
                        />
                    </FormItem>
                    <FormItem
                        label="Confirm Password"
                        invalid={Boolean(errors.confirmPassword)}
                        errorMessage={errors.confirmPassword?.message}
                    >
                        <Controller
                            name="confirmPassword"
                            control={control}
                            render={({ field }) => (
                                <PasswordInput
                                    autoComplete="off"
                                    placeholder="Confirm Password"
                                    {...field}
                                />
                            )}
                        />
                    </FormItem>
                    <CaptchaField
                        key={captchaKey}
                        name="recaptcha"
                        control={control}
                        className="my-4"
                    />
                    <Button
                        block
                        loading={isSubmitting}
                        variant="solid"
                        type="submit"
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit'}
                    </Button>
                </Form>
            ) : (
                <>{children}</>
            )}
        </div>
    )
}

export default ResetPasswordForm
